import axios from 'axios'
import { camelKeys } from 'js-convert-case'
import { getResponseErrorMessage, getAllRecords } from '@/store/utils'
import router from '@/router'

const state = {
  id: null,
  userGroupId: null,
  userGroup: null,
  availableUsers: [],
}

const getState = () => JSON.parse(JSON.stringify(state))

export default {
  namespaced: true,
  state: getState(),
  getters: {
    userGroup (state) {
      return state.userGroup
    },
    payload (state, getters, _, rootGetters) {
      const existingMemberships = state.userGroup.userGroupMemberships || []
      const membershipsAttributes = []
      state.userGroup.accountMembershipIds?.forEach(accountMembershipId => {
        const existingMembership = existingMemberships.find(m => m.accountMembershipId === accountMembershipId)
        if (existingMembership) {
          membershipsAttributes.push({
            id: existingMembership.id,
            account_membership_id: accountMembershipId,
          })
        } else {
          membershipsAttributes.push({
            account_membership_id: accountMembershipId,
          })
        }
      })

      // remove deleted memberships
      existingMemberships.forEach(membership => {
        if (!state.userGroup.accountMembershipIds?.find(accountMembershipId => accountMembershipId === membership.accountMembershipId)) {
          membershipsAttributes.push({ id: membership.id, _destroy: true })
        }
      })

      return {
        user_group: {
          name: state.userGroup.name,
          account_id: rootGetters.accountId,
          user_group_memberships_attributes: membershipsAttributes,
        },
      }
    },
  },
  actions: {
    initialize ({ commit, dispatch,rootGetters }, userGroupId) {
      if (!rootGetters.accountId) {
        throw new Error('Account id has to be set in the state')
      }

      commit('SET_IS_LOADING', true, { root: true })

      const promises = [
        dispatch('fetchUsers'),
      ]
      if (userGroupId) {
        promises.push(dispatch('fetchUserGroup', userGroupId))
      } else {
        commit('SET_USER_GROUP', {})
      }

      return Promise.all(promises).finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    fetchUserGroup ({ commit, rootGetters }, userGroupId) {
      if (!userGroupId || !rootGetters.accountId) {
        throw new Error('userGroupId and accountId should be set in the state')
      }

      return axios
        .get(`/user_groups/${userGroupId}?expand=1`)
        .then(response => commit('SET_USER_GROUP', response.data))
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri pridobivanju skupine uporabnikov, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
        })
    },
    fetchUsers ({ commit, rootGetters }) {
      return getAllRecords(`/account/${rootGetters.accountId}/users`)
        .then(records => commit('SET_AVAILABLE_USERS', records))
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri pridobivanju uporabnikov, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
        })
    },
    setUserGroup ({ commit }, userGroup) {
      commit('SET_USER_GROUP', userGroup)
    },
    updateUserGroupObject ({ commit }, newUserGroup) {
      commit('UPDATE_USER_GROUP_OBJECT', newUserGroup)
    },
    submitUserGroup ({ commit, state, getters }) {
      commit('SET_IS_LOADING', true, { root: true })

      const isEdit = !!state.userGroup.id
      const url = isEdit ? `/user_groups/${state.userGroup.id}` : '/user_groups'
      const method = isEdit ? 'put' : 'post'

      return axios[method](url, getters.payload)
        .then(() => {
          commit('SET_MESSAGE', { text: isEdit ? 'Skupina uporabnikov je bila uspešno posodobljena.' : 'Skupina uporabnikov je bila uspešno ustvarjena.', type: 'success' }, { root: true })
          router.push({ name: 'account_user_groups_path' })
        })
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? (isEdit ? 'Napaka pri posodabljanju skupine uporabnikov, prosimo poskusite kasneje.' : 'Napaka pri ustvarjanju skupine uporabnikov, prosimo poskusite kasneje.')
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
          throw error
        })
        .finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    deleteUserGroup ({ commit }, id) {
      commit('SET_IS_LOADING', true, { root: true })
      return axios
        .delete(`/user_groups/${id}`)
        .then(() => {
          commit('SET_MESSAGE', { text: 'Skupina uporabnikov je bila uspešno izbrisana.', type: 'success' }, { root: true })
        })
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri brisanju skupine uporabnikov, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
          throw error
        })
        .finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    reset ({ commit }) {
      commit('RESET_STATE')
    },
  },
  mutations: {
    SET_USER_GROUP (state, userGroup) {
      const group = camelKeys(userGroup, { recursive: true, recursiveInArray: true })
      group.accountMemberships = (group.userGroupMemberships || []).map(m => {
        return {
          id: m.accountMembershipId,
          accountMembershipId: m.accountMembershipId,
          email: m.user.email,
          name: m.user.fullName,
        }
      })
      state.userGroup = group
    },
    UPDATE_USER_GROUP_OBJECT (state, newUserGroup) {
      state.userGroup = {
        ...state.userGroup,
        ...newUserGroup,
      }
    },
    SET_AVAILABLE_USERS (state, users) {
      state.availableUsers = users.map(user => camelKeys(user))
    },
    RESET_STATE (state) {
      Object.assign(state, getState())
    },
  },
}
